/**
 * Model Storage using OPFS (Origin Private File System) ONLY
 * No fallback to IndexedDB - OPFS is required
 * 
 * This solves Safari iOS 18 IndexedDB issues by using OPFS for model storage
 * OPFS requires HTTPS or localhost (secure context)
 */

let opfsRoot = null;
let opfsAvailable = false;
let opfsInitialized = false;

/**
 * Initialize OPFS storage
 */
const initOPFS = async () => {
  if (opfsInitialized) {
    if (!opfsAvailable) {
      // Reset and try again - maybe it was a transient error
      opfsInitialized = false;
      opfsAvailable = false;
      opfsRoot = null;
    } else {
      return { available: opfsAvailable, root: opfsRoot };
    }
  }
  
  // Check secure context first (OPFS requires HTTPS or localhost)
  // Note: localhost is treated as secure context by browsers, but network IPs are NOT
  const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
  const isSecureContext = window.isSecureContext || (window.location.protocol === 'https:') || isLocalhost;
  const isSafariIOS = /safari/i.test(navigator.userAgent) && !/chrome/i.test(navigator.userAgent) && /iPhone|iPad|iPod/i.test(navigator.userAgent);
  const isSafariDesktop = /safari/i.test(navigator.userAgent) && !/chrome/i.test(navigator.userAgent) && !/iPhone|iPad|iPod/i.test(navigator.userAgent);
  const isSafari = isSafariIOS || isSafariDesktop;
  const isNetworkIP = /^\d+\.\d+\.\d+\.\d+$/.test(window.location.hostname);
  
  try {
    // Safari iOS + HTTP + Network IP = OPFS will NEVER work
    // Note: localhost works because browsers treat it as secure context, but network IPs don't
    if (isSafariIOS && !isSecureContext && isNetworkIP) {
      opfsAvailable = false;
      opfsInitialized = true;
      return { available: false, root: null };
    }
    
    // Check secure context first
    if (!isSecureContext) {
      opfsAvailable = false;
      opfsInitialized = true;
      return { available: false, root: null };
    }
    
    // Check if OPFS API is available
    if (!navigator.storage) {
      opfsAvailable = false;
      opfsInitialized = true;
      return { available: false, root: null };
    }
    
    if (!navigator.storage.getDirectory) {
      opfsAvailable = false;
      opfsInitialized = true;
      return { available: false, root: null };
    }

    // Get OPFS root directory
    try {
      opfsRoot = await navigator.storage.getDirectory();
    } catch (getDirError) {
      opfsAvailable = false;
      opfsInitialized = true;
      return { available: false, root: null };
    }
    
    // Check available methods on Safari iOS
    // Safari iOS doesn't support createWritable() in main thread
    // But it might support writing through other means
    let testHandle;
    try {
      testHandle = await opfsRoot.getFileHandle('__opfs_test__', { create: true });
    } catch (testHandleError) {
      opfsAvailable = false;
      opfsInitialized = true;
      return { available: false, root: null };
    }
    
    // Check specific write methods
    const hasCreateWritable = typeof testHandle.createWritable === 'function';
    const hasCreateSyncAccessHandle = typeof testHandle.createSyncAccessHandle === 'function';
    let writeSuccess = false;
    
    // For Safari iOS, if we can get the file handle, we'll try to write directly
    // Safari iOS might support writing but not expose the methods we expect
    // Let's mark OPFS as available if we can get the root and file handle
    // The actual write will be tested when we try to store the model
    
    // On Safari (iOS or desktop), createWritable() is not supported; createSyncAccessHandle()
    // only works inside a Web Worker. If we have the root and can create file handles,
    // mark OPFS available and we'll use a Web Worker for writes in storeModelInOPFS.
    if (isSafari && !hasCreateWritable && !hasCreateSyncAccessHandle) {
      opfsRoot._isSafariIOS = true;
      opfsRoot._hasDirectWriteMethods = false;
      try {
        await opfsRoot.removeEntry('__opfs_test__');
      } catch (e) {
        // Ignore cleanup errors
      }
      opfsAvailable = true;
      opfsInitialized = true;
      return { available: opfsAvailable, root: opfsRoot };
    }
    // Safari has createSyncAccessHandle but it only works in Workers; main-thread test may throw
    if (isSafari && hasCreateSyncAccessHandle && !hasCreateWritable) {
      try {
        const syncHandle = await testHandle.createSyncAccessHandle();
        syncHandle.close();
        writeSuccess = true;
        opfsRoot._supportsSyncAccessHandle = true;
      } catch (syncError) {
        // Expected on Safari: SyncAccessHandle only works in Web Worker
      }
      try {
        await opfsRoot.removeEntry('__opfs_test__');
      } catch (e) {
        // Ignore cleanup errors
      }
      if (!writeSuccess) {
        opfsRoot._isSafariIOS = true;
        opfsRoot._hasDirectWriteMethods = false;
        opfsAvailable = true;
        opfsInitialized = true;
        return { available: opfsAvailable, root: opfsRoot };
      }
    }
    
    // For other browsers, test write capability normally (writeSuccess already declared above)
    // Try createWritable() first (Chrome/Firefox)
    if (hasCreateWritable) {
      try {
        const writable = await testHandle.createWritable();
        await writable.write(new Uint8Array([1, 2, 3]));
        await writable.close();
        writeSuccess = true;
      } catch (writableError) {
        // createWritable() failed
      }
    }
    
    // Try createSyncAccessHandle() (only works in Web Workers, but check anyway)
    if (!writeSuccess && hasCreateSyncAccessHandle) {
      try {
        const syncHandle = await testHandle.createSyncAccessHandle();
        syncHandle.write(new Uint8Array([1, 2, 3]), { at: 0 });
        syncHandle.close();
        writeSuccess = true;
        opfsRoot._supportsSyncAccessHandle = true;
      } catch (syncError) {
        // createSyncAccessHandle() failed (may require Web Worker)
      }
    }
    
    // Clean up test file
    try {
      await opfsRoot.removeEntry('__opfs_test__');
    } catch (e) {
      // Ignore cleanup errors
    }
    
    if (!writeSuccess && !isSafari) {
      throw new Error('OPFS write test failed. Neither createWritable() nor createSyncAccessHandle() are working.');
    }
    
    if (writeSuccess) {
      opfsAvailable = true;
    } else if (isSafari) {
      opfsAvailable = true;
    }
    
    opfsInitialized = true;
    return { available: opfsAvailable, root: opfsRoot };
  } catch (error) {
    opfsAvailable = false;
    opfsInitialized = true;
    return { available: false, root: null };
  }
};

/**
 * Store model in OPFS
 */
export const storeModelInOPFS = async (key, modelBuffer) => {
  // Match initOPFS: Safari = UA has Safari and does NOT have Chrome (so real Safari, not Chrome on iOS/Android)
  const isSafariIOS = /safari/i.test(navigator.userAgent) && !/chrome/i.test(navigator.userAgent) && /iPhone|iPad|iPod/i.test(navigator.userAgent);
  const isSafariDesktop = /safari/i.test(navigator.userAgent) && !/chrome/i.test(navigator.userAgent) && !/iPhone|iPad|iPod/i.test(navigator.userAgent);
  const isSafari = isSafariIOS || isSafariDesktop;
  
  // Detect Safari version (for error messages and minimum version check)
  const safariVersionMatch = navigator.userAgent.match(/Version\/(\d+\.\d+)/);
  const safariVersion = safariVersionMatch ? safariVersionMatch[1] : 'unknown';
  const safariMajorVersion = safariVersion !== 'unknown' ? parseInt(safariVersion.split('.')[0]) : 0;
  
  // Initialize OPFS first
  const { available, root } = await initOPFS();
  
  if (!available || !root) {
    const isSafari = /safari/i.test(navigator.userAgent) && !/chrome/i.test(navigator.userAgent);
    const msg = isSafari
      ? 'OPFS is not available in this browser. Please use HTTPS or localhost, or try Chrome or Firefox for full support.'
      : 'OPFS is not available. OPFS is required for model storage. Please use HTTPS or localhost.';
    throw new Error(msg);
  }

  try {
    // Check if file already exists and delete it first to avoid quota issues
    try {
      const existingHandle = await root.getFileHandle(key);
      
      // Try standard remove() first
      if (typeof existingHandle.remove === 'function') {
        await existingHandle.remove();
      } else {
        // Safari iOS workaround: use parent directory's removeEntry
        await root.removeEntry(key);
      }
    } catch (notFoundError) {
      if (notFoundError.name !== 'NotFoundError') {
        // Try alternative method
        try {
          await root.removeEntry(key);
        } catch (fallbackError) {
          if (fallbackError.name !== 'NotFoundError') {
            // Error deleting existing file
          }
        }
      }
      // File doesn't exist, which is fine
    }
    
    // Check storage quota before writing
    try {
      if (navigator.storage && navigator.storage.estimate) {
        const estimate = await navigator.storage.estimate();
        const quota = estimate.quota || 0;
        const usage = estimate.usage || 0;
        const available = quota - usage;
        const required = modelBuffer.byteLength;
        
        if (available < required) {
          throw new Error(`Storage quota exceeded. Available: ${(available / 1024 / 1024).toFixed(2)}MB, Required: ${(required / 1024 / 1024).toFixed(2)}MB`);
        }
      }
    } catch (quotaError) {
      throw quotaError;
    }
    
    const fileHandle = await root.getFileHandle(key, { create: true });
    
    // Check for methods in multiple ways (Safari sometimes hides them)
    const hasCreateWritableTypeof = typeof fileHandle.createWritable === 'function';
    const hasCreateWritableDirect = 'createWritable' in fileHandle;
    const hasCreateWritable = hasCreateWritableTypeof || hasCreateWritableDirect;
    
    const hasCreateSyncAccessHandleTypeof = typeof fileHandle.createSyncAccessHandle === 'function';
    const hasCreateSyncAccessHandleDirect = 'createSyncAccessHandle' in fileHandle;
    const hasCreateSyncAccessHandle = hasCreateSyncAccessHandleTypeof || hasCreateSyncAccessHandleDirect;
    
    const useSyncAccessHandle = root._supportsSyncAccessHandle || (!hasCreateWritable && hasCreateSyncAccessHandle);
    
    const writableStartTime = Date.now();
    const chunkSize = 10 * 1024 * 1024; // 10MB chunks
    const totalSize = modelBuffer.byteLength;
    let written = 0;
    
    // For Safari (iOS and desktop), use Web Worker when root was marked for worker or when no main-thread write methods (createSyncAccessHandle only works in workers)
    if (root._isSafariIOS || (isSafari && !hasCreateWritable && !hasCreateSyncAccessHandle)) {
      // Try to write using a Web Worker (createSyncAccessHandle only works in workers)
      // We can't transfer FileSystemFileHandle, so we get the root directory inside the worker
      try {
        const workerCode = `
          self.onmessage = async (e) => {
            const { modelBuffer, key } = e.data;
            try {
              // Get OPFS root directory inside the worker
              const opfsRoot = await navigator.storage.getDirectory();
              const fileHandle = await opfsRoot.getFileHandle(key, { create: true });
              const syncHandle = await fileHandle.createSyncAccessHandle();
              
              const uint8Array = new Uint8Array(modelBuffer);
              const chunkSize = 10 * 1024 * 1024; // 10MB chunks
              const totalSize = uint8Array.length;
              let written = 0;
              
              if (totalSize > chunkSize) {
                while (written < totalSize) {
                  const chunkEnd = Math.min(written + chunkSize, totalSize);
                  const chunk = uint8Array.slice(written, chunkEnd);
                  syncHandle.write(chunk, { at: written });
                  written = chunkEnd;
                  const progress = Math.round((written / totalSize) * 100);
                  if (progress % 10 === 0) {
                    self.postMessage({ type: 'progress', progress });
                  }
                }
              } else {
                syncHandle.write(uint8Array, { at: 0 });
                written = totalSize;
              }
              
              syncHandle.close();
              self.postMessage({ type: 'success' });
            } catch (error) {
              self.postMessage({ type: 'error', error: error.message });
            }
          };
        `;
        
        const blob = new Blob([workerCode], { type: 'application/javascript' });
        const workerUrl = URL.createObjectURL(blob);
        const worker = new Worker(workerUrl);
        
        return new Promise((resolve, reject) => {
          worker.onmessage = (e) => {
            const { type, progress, error } = e.data;
            if (type === 'progress') {
              // Write progress
            } else if (type === 'success') {
              worker.terminate();
              URL.revokeObjectURL(workerUrl);
              resolve(true);
            } else if (type === 'error') {
              worker.terminate();
              URL.revokeObjectURL(workerUrl);
              reject(new Error(`Web Worker write failed: ${error}`));
            }
          };
          
          worker.onerror = (error) => {
            worker.terminate();
            URL.revokeObjectURL(workerUrl);
            reject(new Error(`Web Worker error: ${error.message}`));
          };
          
          // Transfer only the model buffer (ArrayBuffer can be transferred)
          // The worker will get the OPFS root directory itself
          worker.postMessage({ 
            modelBuffer: modelBuffer.buffer,
            key: key
          }, [modelBuffer.buffer]);
        });
      } catch (workerError) {
        throw new Error(`Safari ${safariVersion} OPFS write failed via Web Worker: ${workerError.message}. This might indicate OPFS is not fully supported.`);
      }
    }
    
    // Safari desktop version check - OPFS write support was added in Safari 15.2
    if (isSafari && !isSafariIOS && safariMajorVersion > 0 && safariMajorVersion < 15) {
      throw new Error(`Safari ${safariVersion} does not support OPFS writes. Please update to Safari 15.2 or later. Current version: ${safariVersion}`);
    }
    
    if (useSyncAccessHandle && hasCreateSyncAccessHandle) {
      // Use FileSystemSyncAccessHandle for Safari iOS
      const syncHandle = await fileHandle.createSyncAccessHandle();
      
      const uint8Array = new Uint8Array(modelBuffer);
      
      if (totalSize > chunkSize) {
        while (written < totalSize) {
          const chunkEnd = Math.min(written + chunkSize, totalSize);
          const chunk = uint8Array.slice(written, chunkEnd);
          syncHandle.write(chunk, { at: written });
          written = chunkEnd;
        }
      } else {
        syncHandle.write(uint8Array, { at: 0 });
        written = totalSize;
      }
      
      syncHandle.close();
    } else if (hasCreateWritable) {
      // Use FileSystemWritableFileStream for Chrome/Firefox/Safari desktop
      let writable;
      try {
        writable = await fileHandle.createWritable();
      } catch (writableError) {
        throw new Error(`Failed to create writable stream: ${writableError.message}. Safari version: ${safariVersion}`);
      }
      
      if (totalSize > chunkSize) {
        const uint8Array = new Uint8Array(modelBuffer);
        
        while (written < totalSize) {
          const chunkEnd = Math.min(written + chunkSize, totalSize);
          const chunk = uint8Array.slice(written, chunkEnd);
          await writable.write(chunk);
          written = chunkEnd;
        }
      } else {
        // Write entire buffer for small files
        await writable.write(modelBuffer);
        written = totalSize;
      }
      
      await writable.close();
    } else {
      // Last resort: Try multiple ways to access the methods
      // Safari sometimes hides methods but they're still callable
      
      // Try 1: Direct property access
      if (fileHandle.createWritable) {
        try {
          const writable = await fileHandle.createWritable();
          await writable.write(modelBuffer);
          await writable.close();
          return true;
        } catch (directError) {
          // Direct property access failed
        }
      }
      
      // Try 2: Access via prototype
      const proto = Object.getPrototypeOf(fileHandle);
      if (proto && proto.createWritable) {
        try {
          const writable = await proto.createWritable.call(fileHandle);
          await writable.write(modelBuffer);
          await writable.close();
          return true;
        } catch (protoError) {
          // Prototype access failed
        }
      }
      
      // Try 3: Try calling it anyway (might work even if typeof says it's not a function)
      try {
        const writable = await fileHandle.createWritable();
        await writable.write(modelBuffer);
        await writable.close();
        return true;
      } catch (callError) {
        // Direct call failed
      }
      
      // Try 4: Check if there's a different API (Safari might use a different method name)
      const allMethods = Object.getOwnPropertyNames(Object.getPrototypeOf(fileHandle));
      
      // Try any method that might be related to writing
      const writeMethods = allMethods.filter(m => 
        m.toLowerCase().includes('write') || 
        m.toLowerCase().includes('stream') ||
        m.toLowerCase().includes('writable')
      );
      
      throw new Error(`Safari ${safariVersion} OPFS write methods not available. This might be a Safari bug or missing feature flag. Methods checked: createWritable, createSyncAccessHandle. Available prototype methods: ${allMethods.join(', ')}. Please try: 1) Update Safari to latest version, 2) Enable experimental features in Safari Settings > Advanced > Experimental Features, 3) Try a different browser for testing.`);
    }
    
    return true;
  } catch (error) {
    
    // Provide more helpful error messages
    if (error.message?.includes('quota') || error.name === 'QuotaExceededError') {
      const errorMsg = `Storage quota exceeded. Please clear browser storage or sync your data. ${error.message}`;
      throw new Error(`Failed to store model in OPFS: ${errorMsg}`);
    }
    
    throw new Error(`Failed to store model in OPFS: ${error.message}`);
  }
};

/**
 * Get model from OPFS
 */
export const getModelFromOPFS = async (key) => {
  // Initialize OPFS first
  const { available, root } = await initOPFS();
  
  if (!available || !root) {
    const error = new Error('OPFS is not available. OPFS is required for model storage. Please use HTTPS or localhost.');
    throw error;
  }

  try {
    const fileHandle = await root.getFileHandle(key);
    const file = await fileHandle.getFile();
    
    // Check if this is a binary file (not an ONNX model)
    // Binary files like train_features_mean.bin (5KB) and train_features_covinv.bin (6MB) are valid
    const isBinaryFile = key.includes('train_features') || key.endsWith('.bin');
    
    // Validate file size before reading - minimum 1MB for valid ONNX models only
    // Binary files can be smaller (e.g., train_features_mean.bin is ~5KB)
    if (!isBinaryFile) {
      const MIN_MODEL_SIZE = 1024 * 1024; // 1MB minimum for ONNX models
      if (file.size < MIN_MODEL_SIZE) {
        throw new Error(`Model file is corrupted or incomplete. File size: ${(file.size / 1024 / 1024).toFixed(2)}MB, minimum expected: ${(MIN_MODEL_SIZE / 1024 / 1024).toFixed(2)}MB. Please delete and re-download.`);
      }
    }
    
    const arrayBuffer = await file.arrayBuffer();
    
    // Validate the retrieved buffer
    if (arrayBuffer.byteLength !== file.size) {
      throw new Error(`File read incomplete. Expected ${file.size} bytes, got ${arrayBuffer.byteLength} bytes.`);
    }
    
    // Only validate minimum size for ONNX models
    if (!isBinaryFile && arrayBuffer.byteLength < 1024 * 1024) {
      throw new Error(`Model data is corrupted. Size: ${(arrayBuffer.byteLength / 1024 / 1024).toFixed(2)}MB, minimum expected: 1MB.`);
    }
    
    return arrayBuffer;
  } catch (error) {
    if (error.name === 'NotFoundError') {
      return null;
    }
    
    throw new Error(`Failed to get model from OPFS: ${error.message}`);
  }
};

/**
 * Get file size in OPFS without reading content (for resume/partial checks).
 * @param {string} key - File key
 * @returns {Promise<number>} File size in bytes, or 0 if not found
 */
export const getFileSizeInOPFS = async (key) => {
  const { available, root } = await initOPFS();
  if (!available || !root) return 0;
  try {
    const fileHandle = await root.getFileHandle(key);
    const file = await fileHandle.getFile();
    return file.size;
  } catch (error) {
    if (error.name === 'NotFoundError') return 0;
    throw new Error(`Failed to get file size from OPFS: ${error.message}`);
  }
};

/**
 * Get raw file content from OPFS without size validation (for partial/incomplete files).
 * @param {string} key - File key
 * @returns {Promise<ArrayBuffer|null>} File content or null if not found
 */
export const getFileContentFromOPFS = async (key) => {
  const { available, root } = await initOPFS();
  if (!available || !root) return null;
  try {
    const fileHandle = await root.getFileHandle(key);
    const file = await fileHandle.getFile();
    return await file.arrayBuffer();
  } catch (error) {
    if (error.name === 'NotFoundError') return null;
    throw new Error(`Failed to get file content from OPFS: ${error.message}`);
  }
};

/**
 * Check if model exists in OPFS
 */
export const checkModelInOPFS = async (key) => {
  // Initialize OPFS first
  const { available, root } = await initOPFS();
  
  if (!available || !root) {
    const error = new Error('OPFS is not available. OPFS is required for model storage. Please use HTTPS or localhost.');
    throw error;
  }

  try {
    await root.getFileHandle(key);
    return true;
  } catch (error) {
    if (error.name === 'NotFoundError') {
      return false;
    }
    
    throw new Error(`Failed to check model in OPFS: ${error.message}`);
  }
};

/**
 * Delete model from OPFS
 */
export const deleteModelFromOPFS = async (key) => {
  const { available, root } = await initOPFS();
  
  if (!available || !root) {
    throw new Error('OPFS is not available. OPFS is required for model storage.');
  }

  try {
    const fileHandle = await root.getFileHandle(key);
    
    // Try standard remove() first
    if (typeof fileHandle.remove === 'function') {
      await fileHandle.remove();
      return true;
    } else {
      // Safari iOS workaround: use parent directory's removeEntry
      await root.removeEntry(key);
      return true;
    }
  } catch (error) {
    if (error.name === 'NotFoundError') {
      return true; // Already deleted
    }
    
    // Try alternative method if first one failed
    if (error.name !== 'NotFoundError') {
      try {
        await root.removeEntry(key);
        return true;
      } catch (fallbackError) {
        // Both delete methods failed
      }
    }
    
    return false;
  }
};

/**
 * Clean up old model versions from OPFS
 * Deletes files that start with 'modelv' (old version format) and are not the current key
 */
export const cleanupOldModelsOPFS = async (currentKey) => {
  const { available, root } = await initOPFS();
  
  if (!available || !root) {
    throw new Error('OPFS is not available. OPFS is required for model storage.');
  }

  try {
    // List all files in OPFS root
    const entries = [];
    for await (const [name, handle] of root.entries()) {
      if (handle.kind === 'file' && name.startsWith('modelv') && name !== currentKey) {
        entries.push(name);
      }
    }
    
    // Delete old model files
    for (const key of entries) {
      try {
        await deleteModelFromOPFS(key);
      } catch (error) {
        /* ignore */
      }
    }
    
    return true;
  } catch (error) {
    return false;
  }
};

/**
 * Clean up old model files that don't match the current 3-model system
 * This function identifies and deletes model files that are NOT one of:
 * - drdme_join (main DRDME classifier)
 * - fundus_detector (fundus detector)
 * - eyes_detector (eyes detector)
 * 
 * Also preserves binary files:
 * - train_features_mean
 * - train_features_covinv
 * 
 * This runs only ONCE per user (uses localStorage flag)
 * 
 * @returns {Promise<{deleted: string[], errors: Array<{name: string, error: string}>}>}
 */
export const cleanupAllOldModels = async () => {
  // Check if cleanup has already been done (run only once)
  const CLEANUP_FLAG = 'optha_old_model_cleanup_done';
  const hasRunBefore = localStorage.getItem(CLEANUP_FLAG);
  
  if (hasRunBefore) {
    return { deleted: [], errors: [], skipped: true };
  }

  const { available, root } = await initOPFS();
  
  if (!available || !root) {
    return { deleted: [], errors: [] };
  }

  // Current valid model keys (these should NOT be deleted)
  const validModelKeys = [
    'drdme_join',
    'fundus_detector',
    'eyes_detector',
    'train_features_mean',
    'train_features_covinv'
  ];

  const deleted = [];
  const errors = [];

  try {
    // List all files in OPFS root
    const entries = [];
    for await (const [name, handle] of root.entries()) {
      if (handle.kind === 'file') {
        entries.push(name);
      }
    }
    
    // Identify old model files (any file that's not in the valid list)
    for (const fileName of entries) {
      // Skip valid current models
      if (validModelKeys.includes(fileName)) {
        continue;
      }
      
      // Check if it's likely a model file (large file, or starts with 'model')
      try {
        const fileHandle = await root.getFileHandle(fileName);
        const file = await fileHandle.getFile();
        const fileSizeMB = file.size / 1024 / 1024;
        
        // If file is large (>1MB) or starts with 'model', it's likely an old model
        const isLikelyModel = fileSizeMB > 1 || fileName.startsWith('model') || fileName.includes('model');
        
        if (isLikelyModel) {
          try {
            await deleteModelFromOPFS(fileName);
            deleted.push(fileName);
          } catch (deleteError) {
            errors.push({ name: fileName, error: deleteError.message });
          }
        }
      } catch (fileError) {
        /* could not access file — skip */
      }
    }
    
    // Mark cleanup as done (only if we actually tried to clean up)
    if (entries.length > 0) {
      localStorage.setItem(CLEANUP_FLAG, 'true');
    }
    
    return { deleted, errors };
  } catch (error) {
    return { deleted, errors: [...errors, { name: 'root', error: error.message }] };
  }
};

/**
 * Clear ALL files from OPFS (nuclear option)
 * Use this if clearing site data doesn't work
 */
export const clearAllOPFSFiles = async () => {
  const { available, root } = await initOPFS();
  
  if (!available || !root) {
    return { deleted: 0, errors: [] };
  }

  const deleted = [];
  const errors = [];

  try {
    // List all files and directories
    const entries = [];
    for await (const [name, handle] of root.entries()) {
      entries.push({ name, handle });
    }
    
    // Delete all files
    for (const { name, handle } of entries) {
      try {
        if (handle.kind === 'file') {
          // Try standard remove first
          if (typeof handle.remove === 'function') {
            await handle.remove();
            deleted.push(name);
          } else {
            // Use removeEntry as fallback
            await root.removeEntry(name);
            deleted.push(name);
          }
        } else if (handle.kind === 'directory') {
          // Delete directory (recursive)
          await root.removeEntry(name, { recursive: true });
          deleted.push(name);
        }
      } catch (error) {
        errors.push({ name, error: error.message });
        
        // Try alternative method
        try {
          await root.removeEntry(name, { recursive: true });
          deleted.push(name);
        } catch (fallbackError) {
          // Fallback delete also failed
        }
      }
    }
    
    return { deleted: deleted.length, errors };
  } catch (error) {
    return { deleted: deleted.length, errors: [...errors, { name: 'root', error: error.message }] };
  }
};

// ========== IndexedDB Functions REMOVED ==========
// All IndexedDB fallback code has been removed - OPFS only

/**
 * List all files in OPFS (for debugging)
 */
export const listOPFSFiles = async () => {
  const { available, root } = await initOPFS();
  
  if (!available || !root) {
    return [];
  }
  
  try {
    const files = [];
    for await (const [name, handle] of root.entries()) {
      if (handle.kind === 'file') {
        try {
          const file = await handle.getFile();
          files.push({
            name,
            size: file.size,
            sizeMB: (file.size / 1024 / 1024).toFixed(2),
            lastModified: file.lastModified
          });
        } catch (error) {
          files.push({
            name,
            error: error.message
          });
        }
      }
    }
    return files;
  } catch (error) {
    return [];
  }
};

/**
 * Check model file integrity and return details
 */
export const checkModelIntegrity = async (key) => {
  const { available, root } = await initOPFS();
  
  if (!available || !root) {
    return {
      exists: false,
      error: 'OPFS not available',
      location: 'N/A'
    };
  }
  
  try {
    const fileHandle = await root.getFileHandle(key);
    const file = await fileHandle.getFile();
    
    return {
      exists: true,
      location: 'OPFS',
      fileName: key,
      size: file.size,
      sizeMB: (file.size / 1024 / 1024).toFixed(2),
      isValid: file.size >= 1024 * 1024, // At least 1MB
      expectedSizeMB: 'varies',
      lastModified: new Date(file.lastModified).toISOString(),
      status: file.size >= 1024 * 1024 ? '✅ Valid' : '❌ Corrupted (too small)'
    };
  } catch (error) {
    if (error.name === 'NotFoundError') {
      return {
        exists: false,
        location: 'OPFS',
        fileName: key,
        error: 'File not found'
      };
    }
    return {
      exists: false,
      location: 'OPFS',
      fileName: key,
      error: error.message
    };
  }
};

/**
 * Get storage info for debugging
 */
export const getModelStorageInfo = async () => {
  const { available, root } = await initOPFS();
  
  const info = {
    opfsAvailable: available,
    storageType: 'OPFS',
    userAgent: navigator.userAgent,
    hostname: window.location.hostname,
    protocol: window.location.protocol,
  };
  
  // Add storage quota info if available
  if (navigator.storage && navigator.storage.estimate) {
    try {
      const estimate = await navigator.storage.estimate();
      info.storageQuota = estimate.quota;
      info.storageUsage = estimate.usage;
      info.storageAvailable = estimate.quota - estimate.usage;
      info.storageUsagePercent = estimate.quota > 0 ? ((estimate.usage / estimate.quota) * 100).toFixed(1) : '0';
    } catch (error) {
      // Could not get storage estimate
    }
  }
  
  // List OPFS files for debugging
  if (available && root) {
    try {
      const files = await listOPFSFiles();
      info.opfsFiles = files;
      const totalSize = files.reduce((sum, f) => sum + (f.size || 0), 0);
      info.opfsTotalSizeMB = (totalSize / 1024 / 1024).toFixed(2);
    } catch (error) {
      // Could not list OPFS files
    }
  }
  
  return info;
};

